import { HttpErrorResponse, HttpEventType } from '@angular/common/http';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { defer, forkJoin, Observable, of, Subject, Subscription, timer } from 'rxjs';
import { catchError, concatMap, finalize, map, switchMap, takeUntil } from 'rxjs/operators';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { BaseServices } from '../../kuba.services';
import {
  appendStandardText,
  CompletionCheck,
  completionCheck,
  dueDaysForGrade,
  GradeDistribution,
  gradeDistribution,
  groupSections,
  importanceForGrade,
  isFinding,
  isGraded,
  matchesItemFilter,
  missingRequiredPhoto,
  PHOTO_ACCEPT,
  PHOTO_MAX_COUNT,
  photoFileError,
  photoRequired,
  reportFileName,
  roundProgress,
  SafetyRoundItemFilter,
  SafetyRoundProgress,
  SafetyRoundSection,
  sortedFindings,
  suggestedDeadline,
  toIsoDate,
} from './safety-round.logic';
import { FlexiSafetyRoundLookupService } from './safety-round-lookup.service';
import {
  DeviationImportance,
  SafetyRoundDetail,
  SafetyRoundGrade,
  SafetyRoundItem,
  SafetyRoundLookupOption,
  SafetyRoundPhoto,
  SafetyRoundStatus,
} from './safety-round.models';
import { FlexiSafetyRoundService, saveBlob } from './safety-round.service';

export type SafetyRoundStep = 'details' | 'checkpoints' | 'finish';

/** Debounce before a typed comment is saved. */
const COMMENT_SAVE_DELAY_MS = 800;

/** The three steps, in order. */
const STEPS: SafetyRoundStep[] = ['details', 'checkpoints', 'finish'];

/** One grade button. Order on screen: 1, 2, 3, 4, then I/A. */
interface GradeOption {
  grade: SafetyRoundGrade;
  /** What is printed on the swatch: the number, or "I/A". */
  short: string;
  labelKey: string;
}

/** The deviation form under a finding that has no deviation yet. */
interface DeviationDraft {
  executionUserId: number | null;
  deadline: Date | null;
  /** Once the user picks a date, a grade change no longer moves it. */
  deadlineTouched: boolean;
  topicId: number | null;
  caseId: number | null;
  immediateAction: string;
  saving: boolean;
}

/** One tile of the completion step's grade distribution. */
interface DistributionTile {
  grade: SafetyRoundGrade;
  short: string;
  count: number;
  labelKey: string;
}

type SaveState = 'idle' | 'saving' | 'saved' | 'failed';

/**
 * Flexi › Vernerunder › one round: Details → Checkpoints → Complete.
 *
 * Grades save immediately, comments after a short pause, and every save goes
 * through one queue so they reach the server in the order they were made. A
 * finding (grade 2–4) is followed up as a deviation from the panel under it;
 * once it has one, its grade is locked. A completed round, or a user who is
 * not an author, gets everything read-only.
 */
@Component({
  selector: 'flexi-safety-round',
  templateUrl: './safety-round.component.html',
  styleUrls: ['./safety-round.component.scss'],
  standalone: false,
})
export class SafetyRoundComponent implements OnInit, OnDestroy {
  readonly findingDepartments = new Map<number, number[]>();
  readonly findingFollowupUsers = new Map<number, number>();
  readonly Grade = SafetyRoundGrade;
  readonly Status = SafetyRoundStatus;
  readonly steps = STEPS;
  readonly isFinding = isFinding;
  readonly isGraded = isGraded;
  readonly missingRequiredPhoto = missingRequiredPhoto;
  readonly photoAccept = PHOTO_ACCEPT;
  readonly photoMaxCount = PHOTO_MAX_COUNT;

  readonly gradeOptions: GradeOption[] = [
    { grade: SafetyRoundGrade.Satisfactory, short: '1', labelKey: 'FLEXI_SAFETY_ROUNDS.GRADE.1' },
    { grade: SafetyRoundGrade.ShouldImprove, short: '2', labelKey: 'FLEXI_SAFETY_ROUNDS.GRADE.2' },
    { grade: SafetyRoundGrade.LegalBreach, short: '3', labelKey: 'FLEXI_SAFETY_ROUNDS.GRADE.3' },
    { grade: SafetyRoundGrade.DangerToLifeOrHealth, short: '4', labelKey: 'FLEXI_SAFETY_ROUNDS.GRADE.4' },
    { grade: SafetyRoundGrade.NotApplicable, short: 'I/A', labelKey: 'FLEXI_SAFETY_ROUNDS.GRADE.0' },
  ];

  roundId = 0;
  round: SafetyRoundDetail | null = null;
  loading = true;
  refreshing = 0;
  private loadRequest?: Subscription;
  loadFailed = false;
  step: SafetyRoundStep = 'checkpoints';

  /* ---- Lookups ---- */
  users: SafetyRoundLookupOption[] = [];
  departments: SafetyRoundLookupOption[] = [];
  topics: SafetyRoundLookupOption[] = [];
  caseTypes: SafetyRoundLookupOption[] = [];

  /* ---- Step 1: details ---- */
  title = '';
  location = '';
  departmentId: number | null = null;
  responsibleUserId: number | null = null;
  participants = '';
  detailsSubmitted = false;
  savingDetails = false;
  detailsSaveFailed = false;

  /* ---- Step 2: checkpoints ---- */
  sections: SafetyRoundSection[] = [];
  sectionIndex = 0;
  filter: SafetyRoundItemFilter = 'all';
  query = '';
  visibleItems: SafetyRoundItem[] = [];
  /** "4.2" per item id, from the on-screen order. */
  itemNumbers = new Map<number, string>();
  progress: SafetyRoundProgress = { graded: 0, total: 0, percent: 0, findings: 0, findingsWithoutDeviation: 0 };
  distribution: GradeDistribution = {
    notApplicable: 0, satisfactory: 0, shouldImprove: 0, legalBreach: 0, danger: 0, ungraded: 0,
  };
  /** Items whose comment panel is open. */
  expanded = new Set<number>();
  /** Items whose regulation list is open. */
  refsOpen = new Set<number>();
  drafts = new Map<number, DeviationDraft>();
  saveState: SaveState = 'idle';
  savedAt: Date | null = null;
  bulkBusy = false;

  /** The inline "add checkpoint" form: closed, into the current area, or into a new one. */
  addMode: 'none' | 'item' | 'area' = 'none';
  newItemTitle = '';
  newSectionName = '';
  addingItem = false;

  /* ---- Step 3: finish ---- */
  summary = '';
  confirmed = false;
  completing = false;
  justCompleted = false;
  findings: SafetyRoundItem[] = [];
  completion: CompletionCheck = { ungraded: 0, findingsWithoutDeviation: 0, canComplete: false };
  distributionTiles: DistributionTile[] = [];
  downloading = false;

  /* ---- Photos ---- */
  /** Upload progress per item id, 0–100, while an upload runs. */
  uploading = new Map<number, number>();
  /** Translation key of the last photo error per item id. */
  photoErrors = new Map<number, string>();
  removingPhotoIds = new Set<number>();
  /** The photo open in the preview dialog. */
  preview: SafetyRoundPhoto | null = null;

  /**
   * Admin or a listed HSE (BHT) advisor of the business — as
   * answered by the backend's permissions endpoint. False until it answers.
   */
  canAuthor = false;

  @ViewChild('standardTextMenu') standardTextMenu?: Menu;
  standardTextItems: MenuItem[] = [];

  private readonly destroy$ = new Subject<void>();
  private readonly saveQueue$ = new Subject<number>();
  private readonly commentTimers = new Map<number, Subscription>();
  private savesInFlight = 0;
  private lookupsLoaded = false;
  /** When photo links were last refreshed by re-reading the round. */
  private photoLinksRefreshedAt = 0;

  constructor(
    private api: FlexiSafetyRoundService,
    private lookups: FlexiSafetyRoundLookupService,
    private route: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
    private confirmation: ConfirmationService,
    private notifications: NotificationService,
  ) {}

  get businessId(): number {
    const routedBusiness = this.route.pathFromRoot?.find(
      route => route.routeConfig?.path === 'business/:id'
    );
    return Number(routedBusiness?.snapshot.paramMap.get('id')) || BaseServices.BusinessId;
  }

  get isCompleted(): boolean {
    return this.round?.Status === SafetyRoundStatus.Completed;
  }

  /** Nothing on the page can be changed. */
  get readOnly(): boolean {
    return !this.canAuthor || this.isCompleted;
  }

  get currentSection(): SafetyRoundSection | null {
    return this.sections[this.sectionIndex] ?? null;
  }

  get searching(): boolean {
    return this.query.trim().length > 0;
  }

  /** Comment edits waiting for their debounce, or saves still on the wire. */
  get hasUnsavedChanges(): boolean {
    return this.commentTimers.size > 0 || this.savesInFlight > 0;
  }

  get sectionOptions(): SafetyRoundLookupOption[] {
    return this.sections.map((section, index) => ({
      label: `${section.name} (${section.graded}/${section.items.length})`,
      value: index,
    }));
  }

  ngOnInit(): void {
    this.saveQueue$
      .pipe(
        concatMap(itemId => this.sendGrade(itemId)),
        takeUntil(this.destroy$),
      )
      .subscribe();

    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.roundId = Number(params.get('roundId'));
      this.justCompleted = false;
      this.load();
    });
  }

  ngOnDestroy(): void {
    // Comments still waiting for their debounce are sent rather than lost.
    // These requests are deliberately not tied to destroy$: they are short and
    // finite, and cancelling them is exactly what would lose the text.
    const pending = [...this.commentTimers.keys()];
    this.commentTimers.forEach(sub => sub.unsubscribe());
    this.commentTimers.clear();
    for (const itemId of pending) {
      const item = this.findItem(itemId);
      if (item && !this.readOnly) {
        this.api
          .gradeItem(this.businessId, this.roundId, item.Id, { Grade: item.Grade, Comment: item.Comment })
          .subscribe({ error: () => undefined });
      }
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  /* ======================================================================
     Loading
     ====================================================================== */

  load(): void {
    this.loadRequest?.unsubscribe();
    this.round = null;
    this.loading = true;
    this.loadFailed = false;
    const lookups$ = this.lookupsLoaded
      ? of(null)
      : forkJoin({
          users: this.lookups.businessUsers(this.businessId),
          departments: this.lookups.businessDepartments(this.businessId),
          topics: this.lookups.deviationTopics(this.businessId),
          caseTypes: this.lookups.deviationCaseTypes(this.businessId),
        });
    this.loadRequest = forkJoin({
      round: this.api.get(this.businessId, this.roundId),
      lookups: lookups$,
      permissions: this.api.permissions(this.businessId),
    })
      .pipe(takeUntil(this.destroy$), finalize(() => (this.loading = false)))
      .subscribe({
        next: ({ round, lookups, permissions }) => {
          this.canAuthor = permissions.CanAuthor;
          if (lookups) {
            this.users = lookups.users;
            this.departments = lookups.departments;
            this.topics = lookups.topics;
            this.caseTypes = lookups.caseTypes;
            this.lookupsLoaded = true;
          }
          this.applyRound(round, true);
          this.step = this.initialStep(round);
        },
        error: () => {
          this.round = null;
          this.loadFailed = true;
        },
      });
  }

  /** Re-reads the round, keeping comments the user is still typing. */
  private reload(): Observable<SafetyRoundDetail | null> {
    return defer(() => {
      this.refreshing++;
      return this.api.get(this.businessId, this.roundId);
    }).pipe(
      finalize(() => this.refreshing--),
      map(fresh => {
        this.applyRound(fresh, false);
        return fresh;
      }),
      catchError(() => of(null)),
      takeUntil(this.destroy$),
    );
  }

  private applyRound(round: SafetyRoundDetail, resetForm: boolean): void {
    const previous = this.round;
    if (previous && !resetForm) {
      // A comment whose debounce has not fired yet is newer than the server's.
      for (const itemId of this.commentTimers.keys()) {
        const local = previous.Items.find(i => i.Id === itemId);
        const fresh = round.Items.find(i => i.Id === itemId);
        if (local && fresh) {
          fresh.Comment = local.Comment;
        }
      }
    }
    // Older payloads without the field still render.
    round.Items.forEach(item => (item.Photos = item.Photos ?? []));
    this.photoLinksRefreshedAt = Date.now();
    this.round = round;
    if (resetForm) {
      this.title = round.Title;
      this.location = round.Location || (this.readOnly ? '' : this.defaultLocation());
      this.departmentId = round.BusinessDepartmentId;
      this.responsibleUserId = round.ResponsibleUserId;
      this.participants = round.Participants ?? '';
      this.summary = round.Summary ?? '';
      this.detailsSubmitted = false;
      this.detailsSaveFailed = false;
      this.confirmed = false;
    }
    // Drafts only belong to findings that still need a deviation.
    for (const itemId of [...this.drafts.keys()]) {
      const item = round.Items.find(i => i.Id === itemId);
      if (!item || item.Deviation || !isFinding(item.Grade)) {
        this.drafts.delete(itemId);
      }
    }
    round.Items.filter(item => isFinding(item.Grade) && !item.Deviation).forEach(item => this.ensureDraft(item));
    this.refreshDerived();
  }

  /** A query param wins; otherwise a finished round opens on its summary. */
  private initialStep(round: SafetyRoundDetail): SafetyRoundStep {
    const requested = this.route.snapshot.queryParamMap.get('step') as SafetyRoundStep | null;
    if (requested && STEPS.includes(requested)) {
      return requested;
    }
    return round.Status === SafetyRoundStatus.Completed ? 'finish' : 'checkpoints';
  }

  /** Recomputes everything derived from the items. Called after every change. */
  refreshDerived(): void {
    const items = this.round?.Items ?? [];
    this.sections = groupSections(items);
    if (this.sectionIndex >= this.sections.length) {
      this.sectionIndex = Math.max(0, this.sections.length - 1);
    }
    this.itemNumbers.clear();
    this.sections.forEach((section, s) =>
      section.items.forEach((item, i) => this.itemNumbers.set(item.Id, `${s + 1}.${i + 1}`)));
    this.progress = roundProgress(items);
    this.distribution = gradeDistribution(items);
    this.findings = sortedFindings(items);
    this.completion = completionCheck(items, this.confirmed);
    this.distributionTiles = this.gradeOptions.map(option => ({
      grade: option.grade,
      short: option.short,
      labelKey: option.labelKey,
      count: this.countFor(option.grade),
    }));
    this.updateVisible();
  }

  updateVisible(): void {
    if (this.searching) {
      this.visibleItems = this.sections
        .flatMap(section => section.items)
        .filter(item => matchesItemFilter(item, this.filter, this.query));
    } else {
      this.visibleItems = (this.currentSection?.items ?? [])
        .filter(item => matchesItemFilter(item, this.filter, ''));
    }
  }

  private countFor(grade: SafetyRoundGrade): number {
    switch (grade) {
      case SafetyRoundGrade.NotApplicable:
        return this.distribution.notApplicable;
      case SafetyRoundGrade.Satisfactory:
        return this.distribution.satisfactory;
      case SafetyRoundGrade.ShouldImprove:
        return this.distribution.shouldImprove;
      case SafetyRoundGrade.LegalBreach:
        return this.distribution.legalBreach;
      default:
        return this.distribution.danger;
    }
  }

  private findItem(itemId: number): SafetyRoundItem | undefined {
    return this.round?.Items.find(item => item.Id === itemId);
  }

  /* ======================================================================
     Steps and navigation
     ====================================================================== */

  selectStep(step: SafetyRoundStep): void {
    if (step === this.step) {
      return;
    }
    this.flushComments();
    this.step = step;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { step },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    window.scrollTo({ top: 0 });
  }

  /** Back to the list. Pending comments are flushed by ngOnDestroy. */
  backToList(): void {
    this.router.navigate(['../'], { relativeTo: this.route });
  }

  /** A deviation in the deviation module, under the current business URL prefix. */
  deviationUrl(deviationId: number): string {
    return `${this.router.url.split('/Flexi')[0]}/follow-up/deviations/edit/${deviationId}`;
  }

  /* ======================================================================
     Step 1 — details
     ====================================================================== */

  saveDetails(): void {
    this.detailsSubmitted = true;
    const round = this.round;
    if (this.savingDetails) return;
    if (!round || this.readOnly) {
      this.selectStep('checkpoints');
      return;
    }
    if (!this.title.trim()) {
      return;
    }
    this.detailsSaveFailed = false;
    this.savingDetails = true;
    const request = {
      Title: this.title.trim(),
      Location: this.location.trim() || null,
      BusinessDepartmentId: this.departmentId,
      ResponsibleUserId: this.responsibleUserId,
      Participants: this.participants.trim() || null,
      Summary: this.summary.trim() || null,
    };
    this.api
      .updateDetails(this.businessId, this.roundId, request)
      .pipe(takeUntil(this.destroy$), finalize(() => (this.savingDetails = false)))
      .subscribe({
        next: () => {
          round.Title = request.Title;
          round.Location = request.Location;
          round.BusinessDepartmentId = request.BusinessDepartmentId;
          round.ResponsibleUserId = request.ResponsibleUserId;
          round.ResponsibleUserName = this.users.find(u => u.value === request.ResponsibleUserId)?.label ?? null;
          round.Participants = request.Participants;
          this.notifications.success('UI_NOTIFICATIONS.SAVED');
          this.selectStep('checkpoints');
        },
        // Keep edits and the Details step visible until a save succeeds.
        error: () => { this.detailsSaveFailed = true; }
      });
  }

  departmentName(id: number | null): string {
    return this.departments.find(d => d.value === id)?.label ?? '';
  }

  /* ======================================================================
     Step 2 — checkpoints
     ====================================================================== */

  pickSection(index: number): void {
    this.flushComments();
    this.sectionIndex = index;
    this.query = '';
    this.addMode = 'none';
    this.updateVisible();
  }

  moveSection(delta: number): void {
    const target = this.sectionIndex + delta;
    if (target >= 0 && target < this.sections.length) {
      this.pickSection(target);
      window.scrollTo({ top: 0 });
    }
  }

  pickFilter(filter: SafetyRoundItemFilter): void {
    this.filter = filter;
    this.updateVisible();
  }

  filterCount(filter: SafetyRoundItemFilter): number {
    switch (filter) {
      case 'unanswered':
        return this.progress.total - this.progress.graded;
      case 'findings':
        return this.progress.findings;
      default:
        return this.progress.total;
    }
  }

  /** Grade buttons are disabled read-only, and once a deviation locks the grade. */
  gradeLocked(item: SafetyRoundItem): boolean {
    return this.readOnly || !!item.Deviation || this.drafts.get(item.Id)?.saving === true;
  }

  setGrade(item: SafetyRoundItem, grade: SafetyRoundGrade): void {
    if (this.gradeLocked(item)) {
      return;
    }
    // Pressing the selected grade again clears it.
    const next = item.Grade === grade ? null : grade;
    item.Grade = next;
    if (isFinding(next)) {
      this.ensureDraft(item);
      this.expanded.add(item.Id);
    } else {
      this.drafts.delete(item.Id);
      this.expanded.delete(item.Id);
    }
    this.cancelCommentTimer(item.Id);
    this.saveQueue$.next(item.Id);
    this.refreshDerived();
  }

  toggleExpanded(item: SafetyRoundItem): void {
    if (this.expanded.has(item.Id)) {
      this.expanded.delete(item.Id);
    } else {
      this.expanded.add(item.Id);
    }
  }

  /** Keep the R-mode round open while the regulation is read in its own window. */
  openRegulation(event: MouseEvent, url: string): void {
    event.preventDefault();
    event.stopPropagation();
    try {
      const destination = new URL(url);
      if (destination.protocol !== 'https:' && destination.protocol !== 'http:') {
        return;
      }
      window.open(destination.href, '_blank', 'popup,width=1100,height=850,noopener,noreferrer');
    } catch {
      // Invalid references must never navigate away from the round.
    }
  }

  /** R-mode stores the visited business separately from the signed-in user's business. */
  private defaultLocation(): string {
    try {
      const business = BaseServices.IsRmode
        ? JSON.parse(sessionStorage.getItem('RModeBusiness') || 'null')
        : BaseServices.getUserSettings()?.userBase;
      return typeof business?.BusinessName === 'string' ? business.BusinessName.slice(0, 200) : '';
    } catch {
      return '';
    }
  }

  toggleRefs(item: SafetyRoundItem): void {
    if (this.refsOpen.has(item.Id)) {
      this.refsOpen.delete(item.Id);
    } else {
      this.refsOpen.add(item.Id);
    }
  }

  /** Debounced: the save fires once typing pauses. */
  commentChanged(item: SafetyRoundItem): void {
    if (this.readOnly) {
      return;
    }
    this.cancelCommentTimer(item.Id);
    this.saveState = 'idle';
    this.commentTimers.set(
      item.Id,
      timer(COMMENT_SAVE_DELAY_MS)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.commentTimers.delete(item.Id);
          this.saveQueue$.next(item.Id);
        }),
    );
  }

  openStandardTexts(event: Event, item: SafetyRoundItem): void {
    this.standardTextItems = item.StandardTexts.map(text => ({
      label: text,
      command: () => {
        item.Comment = appendStandardText(item.Comment, text);
        this.expanded.add(item.Id);
        this.commentChanged(item);
      },
    }));
    this.standardTextMenu?.toggle(event);
  }

  /** Photo evidence the template asks for at the item's grade. */
  photoRequired(item: SafetyRoundItem): boolean {
    return photoRequired(item);
  }

  /* ---- Photos ---- */

  /** The backend refuses an eleventh photo with 409. */
  photoLimitReached(item: SafetyRoundItem): boolean {
    return item.Photos.length >= PHOTO_MAX_COUNT;
  }

  canAddPhoto(item: SafetyRoundItem): boolean {
    return !this.readOnly && !this.uploading.has(item.Id) && !this.photoLimitReached(item);
  }

  /**
   * Uploads the picked file. Type and size are checked first so a 6 MB
   * camera photo fails at once instead of after the upload.
   */
  photoPicked(event: Event, item: SafetyRoundItem): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    // Cleared so picking the same file again still fires a change.
    input.value = '';
    if (!file || !this.canAddPhoto(item)) {
      return;
    }
    this.photoErrors.delete(item.Id);
    const invalid = photoFileError(file);
    if (invalid) {
      this.photoErrors.set(item.Id, invalid === 'size' ? 'FLEXI_SAFETY_ROUNDS.PHOTOS.TOO_LARGE' : 'FLEXI_SAFETY_ROUNDS.PHOTOS.INVALID_TYPE');
      return;
    }
    this.uploading.set(item.Id, 0);
    this.expanded.add(item.Id);
    this.api
      .uploadPhoto(this.businessId, this.roundId, item.Id, file)
      .pipe(takeUntil(this.destroy$), finalize(() => this.uploading.delete(item.Id)))
      .subscribe({
        next: httpEvent => {
          if (httpEvent.type === HttpEventType.UploadProgress) {
            const total = httpEvent.total ?? file.size;
            this.uploading.set(item.Id, total ? Math.round((httpEvent.loaded * 100) / total) : 0);
          } else if (httpEvent.type === HttpEventType.Response && httpEvent.body) {
            item.Photos = [...item.Photos, httpEvent.body];
            this.refreshDerived();
          }
        },
        error: (error: HttpErrorResponse) => {
          this.photoErrors.set(item.Id, this.photoErrorKey(error));
          if (error.status === 409) {
            // Completed elsewhere, or the tenth photo came from another device.
            this.reload().subscribe();
          }
        },
      });
  }

  private photoErrorKey(error: HttpErrorResponse): string {
    switch (error.status) {
      case 400:
        return 'FLEXI_SAFETY_ROUNDS.PHOTOS.INVALID';
      case 409:
        return 'FLEXI_SAFETY_ROUNDS.PHOTOS.CONFLICT';
      default:
        return 'FLEXI_SAFETY_ROUNDS.PHOTOS.UPLOAD_FAILED';
    }
  }

  confirmRemovePhoto(item: SafetyRoundItem, photo: SafetyRoundPhoto): void {
    if (this.readOnly || this.removingPhotoIds.has(photo.Id)) {
      return;
    }
    this.confirmation.confirm({
      key: 'fvrRound',
      header: this.translate.instant('CONFIRMATION'),
      message: this.translate.instant('FLEXI_SAFETY_ROUNDS.PHOTOS.CONFIRM_REMOVE', { name: photo.FileName }),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('SWITCH_YES'),
      rejectLabel: this.translate.instant('SWITCH_NO'),
      accept: () => {
        this.removingPhotoIds.add(photo.Id);
        this.photoErrors.delete(item.Id);
        this.api
          .deletePhoto(this.businessId, this.roundId, item.Id, photo.Id)
          .pipe(takeUntil(this.destroy$), finalize(() => this.removingPhotoIds.delete(photo.Id)))
          .subscribe({
            next: () => {
              item.Photos = item.Photos.filter(p => p.Id !== photo.Id);
              if (this.preview?.Id === photo.Id) {
                this.preview = null;
              }
              this.refreshDerived();
            },
            // The interceptor toasts it; a 409 means the round was completed.
            error: () => this.reload().subscribe(),
          });
      },
    });
  }

  openPreview(photo: SafetyRoundPhoto): void {
    this.preview = photo;
  }

  closePreview(): void {
    this.preview = null;
  }

  /**
   * Photo links expire after about 30 minutes. A thumbnail that fails to load
   * re-reads the round for fresh links — at most once a minute, so a link that
   * is broken for another reason cannot loop.
   */
  photoLinkFailed(): void {
    if (Date.now() - this.photoLinksRefreshedAt < 60_000) {
      return;
    }
    this.photoLinksRefreshedAt = Date.now();
    this.reload().subscribe();
  }

  private cancelCommentTimer(itemId: number): void {
    this.commentTimers.get(itemId)?.unsubscribe();
    this.commentTimers.delete(itemId);
  }

  /** Sends every comment still waiting for its debounce. */
  private flushComments(): void {
    const pending = [...this.commentTimers.keys()];
    pending.forEach(itemId => this.cancelCommentTimer(itemId));
    pending.forEach(itemId => this.saveQueue$.next(itemId));
  }

  /**
   * One queued save: the item's grade and comment as they are NOW, so a later
   * edit made while an earlier save was on the wire is never lost.
   */
  private sendGrade(itemId: number): Observable<unknown> {
    const item = this.findItem(itemId);
    if (!item || this.readOnly) {
      return of(null);
    }
    this.savesInFlight++;
    this.saveState = 'saving';
    return this.api
      .gradeItem(this.businessId, this.roundId, item.Id, { Grade: item.Grade, Comment: item.Comment?.trim() ? item.Comment : null })
      .pipe(
        map(() => {
          this.saveState = 'saved';
          this.savedAt = new Date();
          return null;
        }),
        catchError(() => {
          // 409: the round was completed elsewhere, or a deviation now locks the
          // grade. The interceptor has toasted it; resynchronise with the server.
          this.saveState = 'failed';
          return this.reload();
        }),
        finalize(() => {
          this.savesInFlight--;
          this.completion = completionCheck(this.round?.Items ?? [], this.confirmed);
        }),
      );
  }

  /* ---- Deviation panel ---- */

  private ensureDraft(item: SafetyRoundItem): DeviationDraft {
    let draft = this.drafts.get(item.Id);
    if (!draft) {
      draft = {
        executionUserId: null,
        deadline: null,
        deadlineTouched: false,
        topicId: this.topics[0]?.value ?? null,
        caseId: this.caseTypes[0]?.value ?? null,
        immediateAction: '',
        saving: false,
      };
      this.drafts.set(item.Id, draft);
      if (!this.findingDepartments.has(item.Id)) {
        this.findingDepartments.set(item.Id, this.round?.BusinessDepartmentId ? [this.round.BusinessDepartmentId] : []);
      }
    }
    if (!draft.deadlineTouched && this.round) {
      draft.deadline = suggestedDeadline(item.Grade, this.round.DueDays, new Date());
    }
    return draft;
  }

  draftFor(item: SafetyRoundItem): DeviationDraft {
    return this.drafts.get(item.Id) ?? this.ensureDraft(item);
  }

  deadlinePicked(draft: DeviationDraft): void {
    draft.deadlineTouched = true;
  }

  dueDaysFor(item: SafetyRoundItem): number | null {
    return this.round ? dueDaysForGrade(item.Grade, this.round.DueDays) : null;
  }

  importanceKey(importance: DeviationImportance | null | undefined): string {
    return importance ? `FLEXI_SAFETY_ROUNDS.IMPORTANCE.${importance}` : 'FLEXI_SAFETY_ROUNDS.IMPORTANCE.UNKNOWN';
  }

  /** Importance a deviation created from this finding will get. */
  expectedImportanceKey(item: SafetyRoundItem): string {
    return this.importanceKey(importanceForGrade(item.Grade));
  }

  canCreateDeviation(item: SafetyRoundItem, draft: DeviationDraft): boolean {
    return !this.readOnly
      && isFinding(item.Grade)
      && !item.Deviation
      && !draft.saving
      && (this.findingDepartments.get(item.Id)?.length ?? 0) > 0
      && !!this.findingFollowupUsers.get(item.Id)
      && draft.executionUserId !== null
      && draft.deadline !== null
      && draft.topicId !== null
      && draft.caseId !== null
      && (item.Grade !== SafetyRoundGrade.DangerToLifeOrHealth || draft.immediateAction.trim().length > 0)
      // The backend refuses (400) while a photo the template requires is missing.
      && !missingRequiredPhoto(item);
  }

  /**
   * Saves the grade and comment first — the deviation is built from them —
   * then registers the deviation and reads the item back to show its number.
   */
  createDeviation(item: SafetyRoundItem): void {
    const draft = this.draftFor(item);
    if (!this.canCreateDeviation(item, draft) || !draft.deadline) {
      return;
    }
    draft.saving = true;
    this.cancelCommentTimer(item.Id);
    const request = {
      ExecutionUserId: Number(draft.executionUserId),
      Deadline: toIsoDate(draft.deadline),
      TopicId: Number(draft.topicId),
      CaseId: Number(draft.caseId),
      ImmediateAction: draft.immediateAction.trim() || null,
      BusinessDepartmentIds: this.findingDepartments.get(item.Id) ?? [],
      FollowupUserId: this.findingFollowupUsers.get(item.Id) ?? null,
      // The backend attaches the checkpoint's photos itself.
      DeviationAttachments: [],
    };
    this.api
      .gradeItem(this.businessId, this.roundId, item.Id, { Grade: item.Grade, Comment: item.Comment?.trim() ? item.Comment : null })
      .pipe(
        switchMap(() => this.api.createDeviation(this.businessId, item.Id, request)),
        switchMap(() => this.api.getItem(this.businessId, this.roundId, item.Id)),
        takeUntil(this.destroy$),
        finalize(() => (draft.saving = false)),
      )
      .subscribe({
        next: fresh => {
          // Only the saved checkpoint changes; other unsaved comments remain intact.
          item.Deviation = fresh.Deviation;
          item.Photos = fresh.Photos;
          this.refreshDerived();
          this.notifications.success('FLEXI_SAFETY_ROUNDS.DEVIATION.CREATED');
        },
        error: () => {
          this.reload().subscribe();
        },
      });
  }

  /* ---- Adding checkpoints ---- */

  openAdd(mode: 'item' | 'area'): void {
    this.addMode = mode;
    this.newItemTitle = '';
    this.newSectionName = '';
  }

  cancelAdd(): void {
    this.addMode = 'none';
  }

  addItem(): void {
    const sectionName = this.addMode === 'area' ? this.newSectionName.trim() : this.currentSection?.name ?? '';
    const title = this.newItemTitle.trim();
    if (!sectionName || !title || this.addingItem || this.readOnly) {
      return;
    }
    this.addingItem = true;
    this.api
      .addItem(this.businessId, this.roundId, { SectionName: sectionName, Title: title })
      .pipe(
        switchMap(() => this.reload()),
        takeUntil(this.destroy$),
        finalize(() => (this.addingItem = false)),
      )
      .subscribe(() => {
        this.addMode = 'none';
        const index = this.sections.findIndex(section => section.name === sectionName);
        if (index >= 0) {
          this.sectionIndex = index;
          this.query = '';
          this.updateVisible();
        }
      });
  }

  /* ---- Not applicable ---- */

  /** Grades every ungraded item 0 — in one area, or the whole round when null. */
  markRestNotApplicable(sectionName: string | null): void {
    const round = this.round;
    if (!round || this.readOnly || this.bulkBusy) {
      return;
    }
    const targets = round.Items.filter(item =>
      !isGraded(item) && (sectionName === null || item.SectionName === sectionName));
    if (!targets.length) {
      return;
    }
    // Optimistic, so a comment save still queued for one of these items
    // carries the new grade instead of putting it back to ungraded.
    targets.forEach(item => (item.Grade = SafetyRoundGrade.NotApplicable));
    this.refreshDerived();
    this.bulkBusy = true;
    this.api
      .markNotApplicable(this.businessId, this.roundId, { SectionName: sectionName })
      .pipe(
        switchMap(() => this.reload()),
        catchError(() => this.reload()),
        takeUntil(this.destroy$),
        finalize(() => (this.bulkBusy = false)),
      )
      .subscribe();
  }

  /* ---- Jumps from the completion step ---- */

  goToFirstUnanswered(): void {
    const index = this.sections.findIndex(section => section.items.some(item => !isGraded(item)));
    this.jumpTo(index, 'unanswered');
  }

  goToFirstFindingWithoutDeviation(): void {
    const index = this.sections.findIndex(section =>
      section.items.some(item => isFinding(item.Grade) && !item.Deviation));
    this.jumpTo(index, 'findings');
  }

  private jumpTo(sectionIndex: number, filter: SafetyRoundItemFilter): void {
    this.selectStep('checkpoints');
    this.filter = filter;
    this.query = '';
    if (sectionIndex >= 0) {
      this.sectionIndex = sectionIndex;
    }
    this.updateVisible();
  }

  /* ======================================================================
     Step 3 — finish
     ====================================================================== */

  confirmedChanged(): void {
    this.completion = completionCheck(this.round?.Items ?? [], this.confirmed);
  }

  get canSubmitCompletion(): boolean {
    return this.completion.canComplete && !this.completing && !this.hasUnsavedChanges && !this.readOnly;
  }

  complete(): void {
    if (!this.canSubmitCompletion) {
      return;
    }
    this.completing = true;
    this.api
      .complete(this.businessId, this.roundId, { Summary: this.summary.trim() || null, Confirmed: true })
      .pipe(
        switchMap(() => this.api.get(this.businessId, this.roundId)),
        takeUntil(this.destroy$),
        finalize(() => (this.completing = false)),
      )
      .subscribe({
        next: fresh => {
          this.applyRound(fresh, true);
          this.justCompleted = true;
          this.notifications.success('FLEXI_SAFETY_ROUNDS.FINISH.COMPLETED_TOAST');
          window.scrollTo({ top: 0 });
        },
        // A 400 means an item changed underneath; show the current state.
        error: () => this.reload().subscribe(),
      });
  }

  downloadReport(): void {
    const round = this.round;
    if (!round || this.downloading) {
      return;
    }
    this.downloading = true;
    this.api
      .downloadReport(this.businessId, round.Id)
      .pipe(takeUntil(this.destroy$), finalize(() => (this.downloading = false)))
      .subscribe({
        next: file => {
          const fallback = reportFileName(this.translate.instant('FLEXI_SAFETY_ROUNDS.REPORT_FILE_NAME'), round.RoundDate);
          saveBlob(file.blob, file.fileName ?? fallback);
        },
        error: () => undefined
      });
  }

  /** "Lagre og fullfør" is blocked; say why. */
  get completeBlockedKey(): string | null {
    if (this.readOnly) {
      return null;
    }
    if (this.completion.ungraded > 0 || this.completion.findingsWithoutDeviation > 0) {
      return 'FLEXI_SAFETY_ROUNDS.FINISH.BLOCKED_ITEMS';
    }
    if (!this.confirmed) {
      return 'FLEXI_SAFETY_ROUNDS.FINISH.BLOCKED_CONFIRM';
    }
    if (this.hasUnsavedChanges) {
      return 'FLEXI_SAFETY_ROUNDS.SAVING';
    }
    return null;
  }

  /** Marking the whole round's remainder affects many items, so it asks first. */
  confirmMarkAllNotApplicable(): void {
    this.confirmation.confirm({
      key: 'fvrRound',
      header: this.translate.instant('CONFIRMATION'),
      message: this.translate.instant('FLEXI_SAFETY_ROUNDS.FINISH.CONFIRM_MARK_ALL', { count: this.completion.ungraded }),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('SWITCH_YES'),
      rejectLabel: this.translate.instant('SWITCH_NO'),
      accept: () => this.markRestNotApplicable(null),
    });
  }
}
